import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../contexts/OrderContext';
import styles from '../styles/DashboardPage.module.css';
import DownloadHelper from '../components/DownloadHelper';
import FirstSongModal from '../components/FirstSongModal';
import DownloadNotification from '../components/DownloadNotification';
import { auth } from '../firebase';
import { useSearchParams } from 'react-router-dom';

const DashboardPage = () => {
  const { currentUser, deleteUserAccount, sendVerificationEmail } = useAuth();
  const { getUserOrders, requestRevision, orders } = useOrder();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailJustVerified = searchParams.get('emailVerified') === 'success';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentAudio, setCurrentAudio] = useState(null);
  const audioRef = useRef(null);
  const [activeTab, setActiveTab] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  const [selectedLyrics, setSelectedLyrics] = useState('');
  const [selectedSongTitle, setSelectedSongTitle] = useState('');
  const [showNotification, setShowNotification] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [showDownloadHelper, setShowDownloadHelper] = useState(false);
  const [showFirstSongModal, setShowFirstSongModal] = useState(false);
  const [showDownloadNotification, setShowDownloadNotification] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isDownloadGuideVisible, setIsDownloadGuideVisible] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const handleReviewSong = (order) => {
    navigate(`/review/${order.id}`);
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteError('');
      
      if (!confirmDelete) {
        setConfirmDelete(true);
        return;
      }
      
      await deleteUserAccount();
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(err.message || 'Failed to delete account');
    }
  };

  useEffect(() => {
    console.log("Current user:", auth.currentUser);
    console.log("Email verified:", auth.currentUser?.emailVerified);
  }, []);

  useEffect(() => {
    if (emailJustVerified) {
      setSuccess('Your email has been successfully verified!');
      const timeout = setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [emailJustVerified, navigate]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    async function fetchOrders() {
      try {
        await getUserOrders();
        
        if (!localStorage.getItem('firstSongSeen')) {
          const hasCompletedSongs = orders.some(order => 
            (order.status === 'Completed' || order.status === 'Revision Requested') && 
            ((order.songs && order.songs.length > 0) || order.songUrl)
          );
          
          if (hasCompletedSongs) {
            setShowFirstSongModal(true);
            localStorage.setItem('firstSongSeen', 'true');
            setActiveTab('completed');
          }
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Could not load your orders. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    fetchOrders();
  }, [currentUser, getUserOrders, navigate, orders]);

  useEffect(() => {
    setShowNotification(true);
  }, [activeTab]);

  const activeOrders = orders.filter((o) => o.status !== 'Completed' && o.status !== 'Revision Requested');
  const completedOrders = orders.filter((o) => o.status === 'Completed' || o.status === 'Revision Requested');
  const hasCompletedSongs = completedOrders.length > 0;
  const createButtonText = hasCompletedSongs ? "Create A New Song" : "Create Your First Song";

  useEffect(() => {
    console.log('Orders:', orders);
    console.log('Completed Orders:', completedOrders);
    console.log('Active Tab:', activeTab);
    console.log('Show Floating Button (Completed Tab Condition):', activeTab === 'completed' && completedOrders.some(order => (order.songs && order.songs.length > 0) || order.songUrl));
    console.log('Show Floating Button (All Orders Tab Condition):', activeTab === 'all' && orders.some(order => (order.status === 'Completed' || order.status === 'Revision Requested') && ((order.songs && order.songs.length > 0) || order.songUrl)));
  }, [orders, completedOrders, activeTab]);

  const handlePlayAudio = (songUrl) => {
    if (currentAudio === songUrl && audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    } else {
      setCurrentAudio(songUrl);
      if (audioRef.current) {
        audioRef.current.src = songUrl;
        audioRef.current.play();
      }
    }
  };

  const handleDownloadClick = (e, songUrl) => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      return true;
    }
    
    if (!localStorage.getItem('downloadHelperShown')) {
      e.preventDefault();
      setShowDownloadHelper(true);
      localStorage.setItem('downloadHelperShown', 'true');
      sessionStorage.setItem('pendingDownload', songUrl);
      return false;
    }
    
    return true;
  };

  const handleShareSong = (order) => {
    const songUrl = order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null);
    
    if (!songUrl) {
      setError("There's no song available to share yet.");
      setTimeout(() => setError(''), 3000);
      return;
    }

    const songTitle = order.songTitle || `${order.recipientName}'s Song`;
    const shareTitle = `Listen to "${songTitle}" from MakeMeSongs`;
    const shareText = `Check out this custom song created for ${order.recipientName}!`;
    
    if (navigator.share) {
      navigator.share({
        title: shareTitle,
        text: shareText,
        url: songUrl
      })
      .then(() => {
        setSuccess("Song shared successfully!");
        setTimeout(() => setSuccess(''), 3000);
      })
      .catch((error) => {
        console.error('Error sharing:', error);
        if (error.name !== 'AbortError') {
          setError("Couldn't share the song. Try copying the link instead.");
          setTimeout(() => setError(''), 3000);
        }
      });
    } else {
      setShowShareModal(true);
      setShareInfo({
        url: songUrl,
        title: shareTitle,
        text: shareText
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        setError("Couldn't copy to clipboard. Please try again.");
        setTimeout(() => setError(''), 3000);
      });
  };

  const openRevisionModal = (order) => {
    setSelectedOrder(order);
    setRevisionNotes('');
    setShowModal(true);
  };

  const openLyricsModal = (order) => {
    setSelectedLyrics(order.lyrics || '');
    setSelectedSongTitle(order.songTitle || `${order.recipientName}'s Song`);
    setShowLyricsModal(true);
  };

  const openDetailsModal = (order) => {
    setSelectedOrderDetails(order);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedOrderDetails(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setRevisionNotes('');
    setSubmittingRevision(false);
    setError('');
    setSuccess('');
  };

  const closeLyricsModal = () => {
    setShowLyricsModal(false);
    setSelectedLyrics('');
    setSelectedSongTitle('');
  };

  const submitRevisionRequest = async () => {
    if (!revisionNotes.trim()) {
      setError('Please provide details for your revision request.');
      return;
    }
    setSubmittingRevision(true);

    try {
      if (!requestRevision) {
        throw new Error('Revision request functionality is not available. Please contact support.');
      }

      await requestRevision(selectedOrder.id, revisionNotes);
      setSuccess('Your revision request has been submitted.');
      setTimeout(() => closeModal(), 1500);
    } catch (err) {
      console.error('Error requesting revision:', err);
      setError(err.message || 'Could not submit your revision request.');
    } finally {
      setSubmittingRevision(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    if (typeof dateString === 'object' && dateString !== null) {
      if (dateString.earliest && dateString.latest) {
        const earliestDate = new Date(dateString.earliest);
        const latestDate = new Date(dateString.latest);
        
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        const earliestFormatted = earliestDate.toLocaleDateString(undefined, options);
        const latestFormatted = latestDate.toLocaleDateString(undefined, options);
        
        if (earliestFormatted === latestFormatted) {
          return earliestFormatted;
        }
        
        return `${earliestFormatted} - ${latestFormatted}`;
      }
      return 'Processing';
    }
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const renderStatusBadge = (status) => {
    let badgeClass = styles.badgeDefault;
    switch (status) {
      case 'Order Received': badgeClass = styles.badgeBlue; break;
      case 'Writing': badgeClass = styles.badgeTeal; break;
      case 'Recording': badgeClass = styles.badgePurple; break;
      case 'Mixing': badgeClass = styles.badgeOrange; break;
      case 'Revision Requested': badgeClass = styles.badgeYellow; break;
      case 'Completed': badgeClass = styles.badgeGreen; break;
      default: badgeClass = styles.badgeGray;
    }
    return <span className={`${styles.statusBadge} ${badgeClass}`}>{status}</span>;
  };

  const renderProgressTracker = (order) => {
    const steps = ['Order Received', 'Writing', 'Recording', 'Mixing', 'Delivery'];
    const currentStep = steps.indexOf(order.status === 'Revision Requested' ? 'Completed' : order.status);
    const stepPercentage = Math.max(0, (currentStep / (steps.length - 1)) * 100);

    return (
      <div className={styles.progressTracker}>
        <p className={styles.progressTitle}>Current Status</p>
        <div className={styles.progressBarContainer}>
          <div className={styles.progressLine}></div>
          <div className={styles.progressLineActive} style={{ width: `${stepPercentage}%` }}></div>
          <div className={styles.progressSteps}>
            {steps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isActive = index === currentStep;
              return (
                <div key={index} className={styles.progressStepWrapper}>
                  <div className={
                    isCompleted ? styles.stepCircleCompleted :
                    isActive ? styles.stepCircleActive :
                    styles.stepCircle
                  }>
                    {isCompleted ? '✓' : index + 1}
                  </div>
                  <p className={isActive ? styles.stepLabelActive : styles.stepLabel}>
                    {step}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const packageAllowsRevisions = (packageType) => {
    const revisionPackages = ['Premium', 'Deluxe'];
    return revisionPackages.includes(packageType);
  };

  const getMaxRevisions = (packageType) => {
    switch (packageType) {
      case 'Premium': return 1;
      case 'Deluxe': return 2;
      default: return 0;
    }
  };

  const displayRevisionsRemaining = (order) => {
    if (!packageAllowsRevisions(order.package)) {
      return 0;
    }
    
    const maxRevisions = getMaxRevisions(order.package);
    const usedRevisions = order.revisions?.length || 0;
    return Math.max(0, maxRevisions - usedRevisions);
  };

  const canRequestRevision = (order) => {
    const maxRevisions = getMaxRevisions(order.package);
    const usedRevisions = order.revisions?.length || 0;
    const calculatedRemaining = Math.max(0, maxRevisions - usedRevisions);
    
    const packageOk = packageAllowsRevisions(order.package);
    const remainingOk = calculatedRemaining > 0;
    const statusOk = order.status === 'Completed' || order.status === 'Revision Requested';
    
    return packageOk && remainingOk && statusOk;
  };

  const toggleDownloadGuide = () => {
    setIsDownloadGuideVisible(!isDownloadGuideVisible);
  };

  const getNotificationMessage = () => {
    if (activeTab === 'active') {
      if (activeOrders.length === 0) {
        return "You don't have any active songs. Click the button below to create your first song!";
      }
      return "Your song is being created! You'll receive an email when it's ready to listen.";
    } else if (activeTab === 'completed') {
      if (completedOrders.length === 0) {
        return "You don't have any completed songs yet. Once your songs are ready, they'll appear here.";
      }
      return "Your completed songs are ready. Click 'Download MP3' to save them.";
    } else {
      if (orders.length === 0) {
        return "You haven't ordered any songs yet. Click the button below to get started!";
      }
      return "This shows all your songs. Click 'View Details' or 'View Lyrics' to see more information.";
    }
  };

  const getNotificationAction = () => {
    if (activeTab === 'active') {
      if (activeOrders.length === 0) {
        return {
          text: hasCompletedSongs ? "Create A New Song" : "Create Your First Song",
          action: () => navigate('/packages')
        };
      }
      return null;
    } else if (activeTab === 'completed' && completedOrders.length > 0) {
      const firstOrderWithAudio = completedOrders.find(order => order.songUrl || (order.songs && order.songs.length > 0));
      if (firstOrderWithAudio) {
        const songUrl = firstOrderWithAudio.songUrl || (firstOrderWithAudio.songs && firstOrderWithAudio.songs.length > 0 ? firstOrderWithAudio.songs[0].url : null);
        if (songUrl) {
          return {
            text: "Play First Song",
            action: () => handlePlayAudio(songUrl)
          };
        }
      }
    }
    return null;
  };

  const renderTabContent = () => {
    if (activeTab === 'active') {
      if (activeOrders.length === 0) {
        return (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>🎶</p>
            <h2>You don't have any active orders</h2>
            <button 
              className={styles.buttonPrimary} 
              onClick={() => navigate('/packages')}
            >
              {hasCompletedSongs ? "Create A New Song" : "Create Your First Song"}
            </button>
          </div>
        );
      }
      return (
        <div className={styles.ordersList}>
          {activeOrders.map((order) => (
            <div key={order.id} className={styles.orderCard}>
              <div className={styles.orderHeader}>
                <div>
                  <p className={styles.orderNumber}>{order.orderNumber}</p>
                  <p className={styles.orderDate}>Ordered on: {formatDate(order.createdAt)}</p>
                </div>
                {renderStatusBadge(order.status)}
              </div>
              {renderProgressTracker(order)}
              <div className={styles.orderDetails}>
                <p><strong>Package:</strong> {order.package}</p>
                <p><strong>Song For:</strong> {order.recipientName}</p>
                <p><strong>Occasion:</strong> {order.occasion}</p>
                <p><strong>Genres:</strong>{' '}
                  {order.genres.map((genre) => (
                    <span key={genre} className={styles.genreBadge}>{genre}</span>
                  ))}
                </p>
                <p><strong>Estimated Delivery:</strong> {formatDate(order.estimatedDelivery)}</p>
                {packageAllowsRevisions(order.package) && (
                  <p><strong>Revisions:</strong> {displayRevisionsRemaining(order)} Available</p>
                )}
              </div>
              <div className={styles.orderActions}>
                {order.lyrics && (
                  <button className={`${styles.actionButton} ${styles.secondaryButton}`} onClick={() => openLyricsModal(order)}>
                    <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    View Lyrics
                  </button>
                )}
                <button 
                  className={`${styles.actionButton} ${styles.detailsButton}`} 
                  onClick={() => openDetailsModal(order)}
                >
                  <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Order Details
                </button>
                <a 
                  href="mailto:support@mail.makemesongs.com" 
                  className={styles.buttonOutline}
                >
                 Contact Support
                </a>
              </div>
            </div>
          ))}
        </div>
      );
    } else if (activeTab === 'completed') {
      if (completedOrders.length === 0) {
        return (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>🎶</p>
            <h2>You don't have any completed songs yet</h2>
            <button 
              className={styles.buttonPrimary} 
              onClick={() => navigate('/packages')}
            >
              {hasCompletedSongs ? "Create A New Song" : "Create Your First Song"}
            </button>
          </div>
        );
      }
      return (
        <div className={styles.ordersList}>
          {completedOrders.map((order) => (
            <div key={order.id} className={styles.orderCard}>
              <div className={styles.orderHeader}>
                <div>
                  <p className={styles.orderNumber}>{order.orderNumber}</p>
                  <p className={styles.orderDate}>Ordered on: {formatDate(order.createdAt)}</p>
                </div>
                <div className={styles.statusContainer}>
                  {order.status === 'Order Received' && (
                    <button 
                      className={styles.viewActiveButton}
                      onClick={() => setActiveTab('active')}
                    >
                      View Active Orders
                    </button>
                  )}
                  {renderStatusBadge(order.status)}
                </div>
              </div>
              <div className={styles.orderDetails}>
                <p><strong>Package:</strong> {order.package}</p>
                <p><strong>Song For:</strong> {order.recipientName}</p>
                <p><strong>Occasion:</strong> {order.occasion}</p>
                <p><strong>Genres:</strong>{' '}
                  {order.genres.map((genre) => (
                    <span key={genre} className={styles.genreBadge}>{genre}</span>
                  ))}
                </p>
                <p><strong>Delivered On:</strong> {formatDate(order.deliveredAt)}</p>
                {packageAllowsRevisions(order.package) && (
                  <p><strong>Revisions Remaining:</strong> {displayRevisionsRemaining(order)} Available</p>
                )}
              </div>

              {(order.songs && order.songs.length > 0) || order.songUrl ? (
                <div className={styles.audioSection}>
                  {order.songs && order.songs.length > 0 ? (
                    order.songs.map((song, index) => (
                      <div key={index} className={styles.songItem}>
                        <div className={styles.audioInfo}>
                          <p className={styles.songTitle}>
                            {song.name || `${order.recipientName}'s Song${order.songs.length > 1 ? ` (${index + 1})` : ''}`}
                            {song.isMainVersion && <span className={styles.mainVersion}> (Main Version)</span>}
                          </p>
                          <p className={styles.songSubtitle}>
                            {song.description || `${order.genres.join('/')} · Delivered ${formatDate(song.uploadedAt || order.deliveredAt)}`}
                          </p>
                        </div>
                        <div className={styles.audioControls}>
                          <button
                            className={styles.playButton}
                            onClick={() => handlePlayAudio(song.url)}
                            aria-label={currentAudio === song.url && audioRef.current && !audioRef.current.paused ? "Pause Song" : "Play Song"}
                          >
                            {currentAudio === song.url && audioRef.current && !audioRef.current.paused ? '⏸' : '▶'}
                          </button>
                          <div className={styles.downloadSection}>
                            <a 
                              className={styles.buttonSmall} 
                              href={song.url} 
                              download 
                              aria-label="Download MP3"
                              onClick={(e) => handleDownloadClick(e, song.url)}
                            >
                              <span className={styles.downloadText}>Download MP3</span>
                              <span className={styles.downloadHint}>(Right-click & "Save as...")</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.songItem}>
                      <div className={styles.audioInfo}>
                        <p className={styles.songTitle}>
                          {order.songTitle || `${order.recipientName}'s Song`}
                        </p>
                        <p className={styles.songSubtitle}>
                          {order.genres.join('/')} · Delivered {formatDate(order.deliveredAt)}
                        </p>
                      </div>
                      <div className={styles.audioControls}>
                        <button
                          className={styles.playButton}
                          onClick={() => handlePlayAudio(order.songUrl)}
                          aria-label={currentAudio === order.songUrl && audioRef.current && !audioRef.current.paused ? "Pause Song" : "Play Song"}
                        >
                          {currentAudio === order.songUrl && audioRef.current && !audioRef.current.paused ? '⏸' : '▶'}
                        </button>
                        <div className={styles.downloadSection}>
                          <a 
                            className={styles.buttonSmall} 
                            href={order.songUrl} 
                            download 
                            aria-label="Download MP3"
                            onClick={(e) => handleDownloadClick(e, order.songUrl)}
                          >
                            <span className={styles.downloadText}>Download MP3</span>
                            <span className={styles.downloadHint}>(Right-click & "Save as...")</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  {packageAllowsRevisions(order.package) && 
                   displayRevisionsRemaining(order) > 0 && 
                   (order.status === 'Completed' || order.status === 'Revision Requested') && (
                    <button
                      className={styles.buttonOutline}
                      onClick={() => openRevisionModal(order)}
                      style={{ marginTop: '10px' }}
                    >
                      Request Revision ({displayRevisionsRemaining(order)} remaining)
                    </button>
                  )}
                </div>
              ) : null}

              <div className={styles.orderActions}>
                <button 
                  className={styles.buttonPrimary}
                  onClick={() => navigate('/packages')}
                >
                  Order Similar Song
                </button>
                <div className={styles.secondaryActions}>
                  {order.lyrics ? (
                    <button className={`${styles.actionButton} ${styles.secondaryButton}`} onClick={() => openLyricsModal(order)}>
                      <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      View Lyrics
                    </button>
                  ) : (
                    <button className={styles.buttonOutline} disabled>No Lyrics</button>
                  )}
                  {(order.songs && order.songs.length > 0) || order.songUrl ? (
                    <>
                      <button 
                        className={`${styles.actionButton} ${styles.secondaryButton}`} 
                        onClick={() => handlePlayAudio(order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null))}
                      >
                        <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        Play Song
                      </button>
                      <a 
                        className={`${styles.actionButton} ${styles.secondaryButton}`}
                        href={order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null)} 
                        download
                        onClick={(e) => handleDownloadClick(e, order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null))}
                      >
                        <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Song
                      </a>
                    </>
                  ) : null}
                  <button 
                    className={`${styles.actionButton} ${styles.detailsButton}`} 
                    onClick={() => openDetailsModal(order)}
                  >
                    <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    Order Details
                  </button>
                  <button 
                    className={styles.buttonShareGradient}
                    onClick={() => handleShareSong(order)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    Share
                  </button>
                  <button 
                    className={styles.buttonShareGradient}
                    onClick={() => handleReviewSong(order)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M11 17.33a5 5 0 0 1 0-10"></path>
                      <path d="M2 17.33V7a5 5 0 0 1 10 0v10.33"></path>
                      <path d="m15 15.33 3-2.33-3-2.33"></path>
                      <path d="M18 13h-9"></path>
                    </svg>
                    Leave Review
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      if (orders.length === 0) {
        return (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>🎶</p>
            <h2>You don't have any orders yet</h2>
            <button 
              className={styles.buttonPrimary} 
              onClick={() => navigate('/packages')}
            >
              {hasCompletedSongs ? "Create A New Song" : "Create Your First Song"}
            </button>
          </div>
        );
      }
      return (
        <div className={styles.ordersList}>
          {orders.map((order) => (
            <div key={order.id} className={styles.orderCard}>
              <div className={styles.orderHeader}>
                <p className={styles.orderNumber}>{order.orderNumber}</p>
                <div className={styles.statusContainer}>
                  {order.status === 'Order Received' && (
                    <button 
                      className={styles.viewActiveButton}
                      onClick={() => setActiveTab('active')}
                    >
                      View Active Orders
                    </button>
                  )}
                  {renderStatusBadge(order.status)}
                </div>
              </div>
              <div className={styles.orderDateRow}>
                <p>Ordered: {formatDate(order.createdAt)}</p>
                <p>
                  {order.status === 'Completed' || order.status === 'Revision Requested'
                    ? `Delivered: ${formatDate(order.deliveredAt)}`
                    : `Est. Delivery: ${formatDate(order.estimatedDelivery)}`}
                </p>
              </div>
              <div className={styles.orderDetails}>
                <p><strong>{order.package} Package</strong></p>
                <p>For: {order.recipientName}</p>
                <p>Occasion: {order.occasion}</p>
                {packageAllowsRevisions(order.package) && (
                  <p><strong>Revisions:</strong> {displayRevisionsRemaining(order)} Available</p>
                )}
              </div>
              <div className={styles.orderActions}>
                {order.lyrics ? (
                  <button className={`${styles.actionButton} ${styles.secondaryButton}`} onClick={() => openLyricsModal(order)}>
                    <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    View Lyrics
                  </button>
                ) : (
                  <button 
                    className={styles.buttonOutlineSmall} 
                    onClick={() => {
                      if (order.status !== 'Completed' && order.status !== 'Revision Requested') {
                        setActiveTab('active');
                      } else {
                        setActiveTab('completed');
                      }
                    }}
                  >
                    View Details
                  </button>
                )}
                <button 
                  className={`${styles.actionButton} ${styles.detailsButton}`} 
                  onClick={() => openDetailsModal(order)}
                >
                  <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  Order Details
                </button>
                {(order.status === 'Completed' || order.status === 'Revision Requested') && 
                 ((order.songs && order.songs.length > 0) || order.songUrl) ? (
                  <>
                    <button
                      className={`${styles.actionButton} ${styles.secondaryButton}`} 
                      onClick={() => handlePlayAudio(order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null))}
                    >
                      <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                      Play Song
                    </button>
                    <a 
                      className={`${styles.actionButton} ${styles.secondaryButton}`}
                      href={order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null)} 
                      download
                      onClick={(e) => handleDownloadClick(e, order.songUrl || (order.songs && order.songs.length > 0 ? order.songs[0].url : null))}
                    >
                      <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download Song
                    </a>
                    <button 
                      className={styles.buttonShareGradient}
                      onClick={() => handleShareSong(order)}
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                      </svg>
                      Share
                    </button>
                    <button 
                      className={styles.buttonShareGradient}
                      onClick={() => handleReviewSong(order)}
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M11 17.33a5 5 0 0 1 0-10"></path>
                        <path d="M2 17.33V7a5 5 0 0 1 10 0v10.33"></path>
                        <path d="m15 15.33 3-2.33-3-2.33"></path>
                        <path d="M18 13h-9"></path>
                      </svg>
                      Leave Review
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className={styles.spinnerContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      {(error || success) && (
        <div className={styles.alertContainer}>
          {error && <div className={styles.alertError}>{error}</div>}
          {success && <div className={styles.alertSuccess}>{success}</div>}
        </div>
      )}

      <div className={styles.dashboardHeader}>
        <h1 className={styles.dashboardTitle}>My Songs</h1>
        <div className={styles.headerButtons}>
          <button className={styles.buttonOutline} onClick={() => setShowProfileModal(true)}>
            Account Settings
          </button>
          <button className={styles.buttonPrimary} onClick={() => navigate('/packages')}>
            Order New Song
          </button>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={activeTab === 'active' ? `${styles.tabButton} ${styles.tabButtonActive}` : styles.tabButton}
          onClick={() => setActiveTab('active')}
        >
          Active Orders ({activeOrders.length})
        </button>
        <button
          className={activeTab === 'completed' ? `${styles.tabButton} ${styles.tabButtonActive}` : styles.tabButton}
          onClick={() => setActiveTab('completed')}
        >
          Completed ({completedOrders.length})
        </button>
        <button
          className={activeTab === 'all' ? `${styles.tabButton} ${styles.tabButtonActive}` : styles.tabButton}
          onClick={() => setActiveTab('all')}
        >
          All Orders ({orders.length})
        </button>
      </div>

      <div className={styles.tabContent}>{renderTabContent()}</div>

      <audio ref={audioRef} onEnded={() => setCurrentAudio(null)} />

      {showNotification && (
        <div className={styles.dashboardNotification}>
          {activeTab === 'active' && activeOrders.length === 0 ? (
            <>
              {hasCompletedSongs ? (
                <p>You don't have any active songs. Click the button below to create a new song!</p>
              ) : (
                <p>You don't have any active songs. Click the button below to create your first song!</p>
              )}
              {getNotificationAction() && (
                <button 
                  className={styles.notificationButton}
                  onClick={getNotificationAction().action}
                >
                  {getNotificationAction().text}
                </button>
              )}
            </>
          ) : (
            <p>{getNotificationMessage()}</p>
          )}
          {activeTab !== 'active' && getNotificationAction() && (
            <button 
              className={styles.notificationButton}
              onClick={getNotificationAction().action}
            >
              {getNotificationAction().text}
            </button>
          )}
          <button 
            className={styles.notificationCloseButton}
            onClick={() => setShowNotification(false)}
          >
            ✕
          </button>
        </div>
      )}

      {activeTab === 'completed' && completedOrders.length > 0 && showDownloadNotification && (
        <DownloadNotification 
          isVisible={true}
          onClose={() => setShowDownloadNotification(false)}
          onShowHelp={() => setShowDownloadHelper(true)}
        />
      )}

      {(activeTab === 'completed' && completedOrders.some(order => (order.songs && order.songs.length > 0) || order.songUrl)) || 
       (activeTab === 'all' && orders.some(order => (order.status === 'Completed' || order.status === 'Revision Requested') && 
       ((order.songs && order.songs.length > 0) || order.songUrl))) ? (
        <button 
          className={styles.floatingDownloadHelpButton}
          onClick={toggleDownloadGuide}
        >
          🎵 Download Help
        </button>
      ) : null}

      {isDownloadGuideVisible && (
        <div className={styles.downloadGuideModal}>
          <button 
            className={styles.downloadGuideClose} 
            onClick={() => setIsDownloadGuideVisible(false)}
          >
            ✕
          </button>
          <h3>How to Download Your Songs</h3>
          <ol>
            <li>Find your song below and click the Download MP3 button</li>
            <li>
              <strong>On a computer:</strong> Right-click on the Download button and select{' '}
              <strong>"Save link as..."</strong> or <strong>"Download linked file"</strong>
            </li>
            <li>
              <strong>On a mobile device:</strong> Press and hold the Download button, then select the download option
            </li>
          </ol>
          <button 
            className={styles.viewFullGuideLink}
            onClick={() => {
              setIsDownloadGuideVisible(false);
              setShowDownloadHelper(true);
            }}
          >
            View Full Step-by-Step Guide
          </button>
        </div>
      )}

      {showDetailsModal && selectedOrderDetails && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.modalClose} onClick={closeDetailsModal}>×</button>
            <h2 className={styles.modalTitle}>Order Details</h2>
            
            <div className={styles.orderDetailsModal}>
              <h3>Basic Information</h3>
              <div className={styles.detailsRow}>
                <strong>Order Number:</strong> {selectedOrderDetails.orderNumber}
              </div>
              <div className={styles.detailsRow}>
                <strong>Package:</strong> {selectedOrderDetails.package}
              </div>
              <div className={styles.detailsRow}>
                <strong>Song For:</strong> {selectedOrderDetails.recipientName}
              </div>
              <div className={styles.detailsRow}>
                <strong>Occasion:</strong> {selectedOrderDetails.occasion}
              </div>
              <div className={styles.detailsRow}>
                <strong>Ordered On:</strong> {formatDate(selectedOrderDetails.createdAt)}
              </div>
              <div className={styles.detailsRow}>
                <strong>Estimated Delivery:</strong> {formatDate(selectedOrderDetails.estimatedDelivery)}
              </div>
              
              <h3>Music Preferences</h3>
              <div className={styles.detailsRow}>
                <strong>Genres:</strong>{' '}
                {selectedOrderDetails.genres && selectedOrderDetails.genres.join(', ')}
              </div>
              
              {selectedOrderDetails.details && (
                <>
                  {selectedOrderDetails.details.vocalPreference && (
                    <div className={styles.detailsRow}>
                      <strong>Vocal Preference:</strong>{' '}
                      {selectedOrderDetails.details.vocalPreference === 'no-preference' 
                        ? 'No Preference' 
                        : selectedOrderDetails.details.vocalPreference === 'male'
                          ? 'Male Vocalist'
                          : selectedOrderDetails.details.vocalPreference === 'female'
                            ? 'Female Vocalist'
                            : selectedOrderDetails.details.vocalPreference === 'duet'
                              ? 'Duet'
                              : selectedOrderDetails.details.vocalPreference}
                    </div>
                  )}
                  
                  {selectedOrderDetails.details.songTone && (
                    <div className={styles.detailsRow}>
                      <strong>Song Tone:</strong>{' '}
                      {selectedOrderDetails.details.songTone.charAt(0).toUpperCase() + 
                       selectedOrderDetails.details.songTone.slice(1)}
                    </div>
                  )}
                  
                  {selectedOrderDetails.details.favoriteArtists && (
                    <div className={styles.detailsRow}>
                      <strong>Favorite Artists:</strong> {selectedOrderDetails.details.favoriteArtists}
                    </div>
                  )}
                  
                  {selectedOrderDetails.details.rushDelivery && (
                    <div className={styles.detailsRow}>
                      <strong>Delivery Speed:</strong> <span className={styles.rushDelivery}>Rush Delivery (24-48 hours)</span>
                    </div>
                  )}
                  
                  <h3 className={styles.storyHeading}>Tell Us Your Story</h3>
                  
                  {selectedOrderDetails.details.songStory ? (
                    <div className={styles.storySection}>
                      <div className={styles.storyContent}>
                        <p className={styles.storyText}>
                          {selectedOrderDetails.details.songStory.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.storySection}>
                      <div className={styles.storyContent}>
                        <p className={styles.storyText}>No story details available</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrderDetails.details.keyPoints && (
                    <div className={styles.detailsRow}>
                      <strong>Key Points to Include:</strong>
                      <p className={styles.keyPointsText}>
                        {selectedOrderDetails.details.keyPoints.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </p>
                    </div>
                  )}
                  
                  {selectedOrderDetails.details.additionalNotes && (
                    <div className={styles.detailsRow}>
                      <strong>Additional Notes:</strong>
                      <p>
                        {selectedOrderDetails.details.additionalNotes.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </p>
                    </div>
                  )}
                </>
              )}
              
              <div className={styles.modalActions}>
                <button 
                  className={styles.buttonPrimary} 
                  onClick={closeDetailsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.modalClose} onClick={closeModal}>×</button>
            <h2 className={styles.modalTitle}>Request a Revision</h2>
            {selectedOrder && (
              <p className={styles.modalText}>
                You have {displayRevisionsRemaining(selectedOrder)} revision
                {displayRevisionsRemaining(selectedOrder) !== 1 ? 's' : ''} remaining for this song.
                Please describe what you'd like changed:
              </p>
            )}
            {error && <div className={styles.alertError}>{error}</div>}
            {success && <div className={styles.alertSuccess}>{success}</div>}
            <textarea
              className={styles.modalTextarea}
              placeholder="Please be specific about what you'd like changed..."
              rows={6}
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
            />
            <div className={styles.modalActions}>
              <button className={styles.buttonOutline} onClick={closeModal} disabled={submittingRevision}>
                Cancel
              </button>
              <button className={styles.buttonPrimary} onClick={submitRevisionRequest} disabled={submittingRevision}>
                {submittingRevision ? 'Submitting...' : 'Submit Revision Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLyricsModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.modalClose} onClick={closeLyricsModal}>×</button>
            <h2 className={styles.modalTitle}>{selectedSongTitle} - Lyrics</h2>
            <div className={styles.lyricsContainer}>
              {selectedLyrics ? (
                <pre className={styles.lyricsText}>{selectedLyrics}</pre>
              ) : (
                <p className={styles.emptyLyrics}>No lyrics available for this song yet.</p>
              )}
            </div>
            <div className={styles.modalActions}>
              <button className={styles.buttonPrimary} onClick={closeLyricsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.modalClose} onClick={() => {
              setShowProfileModal(false);
              setConfirmDelete(false);
              setConfirmPassword('');
              setDeleteError('');
            }}>×</button>
            <h2 className={styles.modalTitle}>Account Settings</h2>
            
            <div className={styles.profileSection}>
              <h3>Profile Information</h3>
              <p><strong>Name:</strong> {currentUser?.displayName || 'Not provided'}</p>
              <p><strong>Email:</strong> {currentUser?.email}</p>
              <p><strong>Email Verified:</strong> {currentUser?.emailVerified ? 'Yes' : 'No'}</p>
              
              {!currentUser?.emailVerified && (
                <button 
                  className={styles.buttonPrimary}
                  onClick={() => {
                    sendVerificationEmail()
                      .then(() => setSuccess('Verification email sent!'))
                      .catch(err => setError('Failed to send verification email'));
                  }}
                >
                  Verify Email
                </button>
              )}
            </div>
            
            <div className={styles.dangerZone}>
              <h3>Danger Zone</h3>
              <p className={styles.warningText}>Deleting your account is permanent. All your data will be removed.</p>
              
              {deleteError && <div className={styles.alertError}>{deleteError}</div>}
              
              {!confirmDelete ? (
                <button 
                  className={styles.buttonDanger}
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </button>
              ) : (
                <>
                  <p>Are you sure you want to delete your account? This action cannot be undone.</p>
                  <button 
                    className={styles.buttonDanger}
                    onClick={handleDeleteAccount}
                  >
                    Confirm Account Deletion
                  </button>
                  <button 
                    className={styles.buttonOutline}
                    onClick={() => setConfirmDelete(false)}
                    style={{ marginTop: '10px' }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <DownloadHelper 
        isVisible={showDownloadHelper} 
        onClose={() => setShowDownloadHelper(false)}
      />

      <FirstSongModal 
        isVisible={showFirstSongModal}
        onClose={() => setShowFirstSongModal(false)}
        onShowDownloadHelp={() => setShowDownloadHelper(true)}
      />

      {showShareModal && shareInfo && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.modalClose} onClick={() => setShowShareModal(false)}>×</button>
            <h2 className={styles.modalTitle}>Share Your Song</h2>
            
            <div className={styles.shareModalContent}>
              <p className={styles.shareText}>{shareInfo.text}</p>
              
              <div className={styles.shareInputGroup}>
                <input
                  type="text"
                  className={styles.shareUrlInput}
                  value={shareInfo.url}
                  readOnly
                />
                <button 
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(shareInfo.url)}
                >
                  {copySuccess ? 'Copied! ✓' : 'Copy'}
                </button>
              </div>
              
              <div className={styles.shareSocialButtons}>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareInfo.url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.socialButton} ${styles.facebookButton}`}
                >
                  Share on Facebook
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareInfo.text)}&url=${encodeURIComponent(shareInfo.url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.socialButton} ${styles.twitterButton}`}
                >
                  Share on Twitter
                </a>
                <a 
                  href={`mailto:?subject=${encodeURIComponent(shareInfo.title)}&body=${encodeURIComponent(shareInfo.text + '\n\n' + shareInfo.url)}`}
                  className={`${styles.socialButton} ${styles.emailButton}`}
                >
                  Share via Email
                </a>
              </div>
            </div>
            
            <div className={styles.modalActions}>
              <button 
                className={styles.buttonPrimary} 
                onClick={() => setShowShareModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;